import { useState, type ReactNode, type ComponentPropsWithoutRef } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { searchIncludes, searchNormalize } from "@/lib/search-normalize";

export interface SearchableSelectOption {
  value: string;
  /** Text shown in the list row and in the trigger when selected. */
  label: string;
  /** Extra text matched by the search box but not displayed (e.g. address + name). */
  keywords?: string;
  /** Rich list-row content (badges, icons). `label` still drives the trigger text and search. */
  labelNode?: ReactNode;
  /** Per-item data-testid; falls back to `${testId}-option-${value}`. */
  testId?: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (v: string) => void;
  options: SearchableSelectOption[];
  /** Trigger text when `value` matches no option. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  testId?: string;
  /** Classes for the trigger button (sizing/width), like a SelectTrigger className. */
  className?: string;
  /** Rendered before the label inside the trigger (e.g. a Filter icon). */
  leadingIcon?: ReactNode;
  /** Set when rendered inside a Dialog/Sheet so the popover traps focus correctly. */
  modal?: boolean;
  align?: "start" | "end";
  disabled?: boolean;
  /** Extra attributes spread onto the trigger button (title, data-tour, …). */
  triggerProps?: ComponentPropsWithoutRef<"button"> & { [dataAttr: `data-${string}`]: string | undefined };
}

/**
 * Drop-in searchable replacement for the single-value filter `<Select>`:
 * same trigger look, but opening it shows a type-to-filter search box above
 * the option list. Filtering is done here (`shouldFilter={false}`) via the
 * null-safe search helpers, matching label + keywords.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  testId,
  className,
  leadingIcon,
  modal = false,
  align = "start",
  disabled,
  triggerProps,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((o) => o.value === value);
  const needle = searchNormalize(search.trim());
  const filtered = needle
    ? options.filter((o) => searchIncludes(`${o.label} ${o.keywords ?? ""}`, needle))
    : options;
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setSearch("");
      }}
      modal={modal}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
            className,
          )}
          data-testid={testId}
          {...triggerProps}
        >
          {leadingIcon}
          <span className={cn("text-left", !selected && "text-muted-foreground")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0"
        align={align}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => {
                    onValueChange(opt.value);
                    setSearch("");
                    setOpen(false);
                  }}
                  data-testid={opt.testId ?? (testId ? `${testId}-option-${opt.value}` : undefined)}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4 shrink-0", value === opt.value ? "opacity-100" : "opacity-0")}
                  />
                  {opt.labelNode ?? <span className="truncate whitespace-pre">{opt.label}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
